import type {
  FetchNextPageOptions,
  InfiniteData,
  QueryClient,
  RefetchOptions,
} from '@tanstack/react-query';

import { useEffect, useRef } from 'react';

interface IUseFetchAllPagesArgs<TData> {
  data: TData | undefined;
  hasNextPage: boolean | undefined;
  isFetchingNextPage: boolean;
  isError: boolean;
  enabled: boolean;
  fetchNextPage: (options?: FetchNextPageOptions) => unknown;
  onAllPagesLoaded: (data: TData) => void;
}

// Effect-driven auto-pagination for the initial load path.
// `cancelRefetch: false` so concurrent observers of the same query
// coalesce to one in-flight HTTP request instead of cancelling/restarting
// (appFetcher does not honor AbortSignal).
export const useFetchAllPages = <TData>({
  data,
  hasNextPage,
  isFetchingNextPage,
  isError,
  enabled,
  fetchNextPage,
  onAllPagesLoaded,
}: IUseFetchAllPagesArgs<TData>): void => {
  const onAllPagesLoadedRef = useRef(onAllPagesLoaded);

  useEffect(() => {
    onAllPagesLoadedRef.current = onAllPagesLoaded;
  }, [onAllPagesLoaded]);

  useEffect(() => {
    if (!enabled || isError) return;
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage({ cancelRefetch: false });
    }
  }, [enabled, hasNextPage, isFetchingNextPage, isError, fetchNextPage]);

  useEffect(() => {
    if (!enabled || isError) return;
    if (!data) return;
    if (hasNextPage || isFetchingNextPage) return;
    onAllPagesLoadedRef.current(data);
  }, [enabled, data, hasNextPage, isFetchingNextPage, isError]);
};

interface IRefetchAllPagesArgs<TPage> {
  queryClient: QueryClient;
  queryKey: unknown[];
  refetch: (options?: RefetchOptions) => Promise<{ data?: InfiniteData<TPage> }>;
  fetchNextPage: (options?: FetchNextPageOptions) => Promise<{ data?: InfiniteData<TPage> }>;
  hasMorePages: (data: InfiniteData<TPage> | undefined) => boolean;
}

// Module-level dedupe map: when two callers run refetchAllPages on the same
// queryKey concurrently, the second one piggybacks on the first's promise
// instead of starting a competing refetch (which would cancel the first via
// `cancelRefetch: true` default and continue its own loop on partial data).
const inFlightRefetchAllPages = new Map<string, Promise<unknown>>();

// Imperative full-pagination loop for user-triggered refetch.
// Slices the cache to one page first so the next refetch starts from a fresh
// cursor — avoids drift when list items were added/removed since last fetch.
// `refetch` uses default `cancelRefetch: true` so an in-flight background
// load gets cancelled and replaced; the loop's `fetchNextPage` uses
// `cancelRefetch: false` so it dedupes with sibling observers' calls.
// Throws on any failure — caller decides recovery (fail-safe vs toast vs ignore).
export const refetchAllPages = <TPage>({
  queryClient,
  queryKey,
  refetch,
  fetchNextPage,
  hasMorePages,
}: IRefetchAllPagesArgs<TPage>): Promise<InfiniteData<TPage> | undefined> => {
  const dedupeKey = JSON.stringify(queryKey);
  const existing = inFlightRefetchAllPages.get(dedupeKey);
  if (existing) {
    return existing as Promise<InfiniteData<TPage> | undefined>;
  }

  const promise = (async (): Promise<InfiniteData<TPage> | undefined> => {
    try {
      queryClient.setQueryData<InfiniteData<TPage>>(queryKey, (oldData) => {
        if (!oldData) return undefined;
        return {
          ...oldData,
          pages: oldData.pages.slice(0, 1),
          pageParams: oldData.pageParams.slice(0, 1),
        };
      });

      let result = await refetch({ throwOnError: true });
      while (hasMorePages(result.data)) {
        result = await fetchNextPage({ throwOnError: true, cancelRefetch: false });
      }
      return result.data;
    } finally {
      inFlightRefetchAllPages.delete(dedupeKey);
    }
  })();

  inFlightRefetchAllPages.set(dedupeKey, promise);
  return promise;
};
